/**
 * Cozzee bundle selector — vertical option cards.
 * Test template only. Catalog from [data-mp-family-catalog].
 */
(function () {
  'use strict';

  const TIER_DISCOUNT = { 1: 0, 2: 0.15, 3: 0.25, 4: 0.3 };

  function getDiscountRate(count) {
    if (count >= 4) return TIER_DISCOUNT[4];
    return TIER_DISCOUNT[count] || 0;
  }

  function pricePerBagCents(baseCents, count) {
    return Math.round(baseCents * (1 - getDiscountRate(count)));
  }

  function bundleTotalCents(baseCents, count) {
    return pricePerBagCents(baseCents, count) * count;
  }

  function formatMoney(cents, symbol) {
    const dollars = Math.round(Number(cents) / 100);
    return `${symbol}${dollars}`;
  }

  function aggregateItems(slots) {
    const map = new Map();
    slots.forEach(function (slot) {
      if (!slot.variantId) return;
      const key = String(slot.variantId);
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries()).map(function ([id, quantity]) {
      return { id: Number(id), quantity };
    });
  }

  class CozzeeBundleSelector {
    constructor(root) {
      this.root = root;
      this.productInfo = root.closest('product-info');
      if (!this.productInfo) return;

      this.basePrice = Number(root.dataset.basePrice) || 0;
      this.currencySymbol = root.dataset.currencySymbol || '$';
      this.tier = 1;
      this.catalog = [];
      this.slotState = { 2: [], 3: [], 4: [] };
      this.selectionContinuity = [];
      this.minSlots = { 2: 2, 3: 3, 4: 4 };

      this.optionCards = root.querySelectorAll('[data-mp-cozzee-option]');
      this.buy1Mount = root.querySelector('[data-mp-cozzee-buy1-mount]');
      this.addSlotButton = root.querySelector('[data-mp-cozzee-add-slot]');
      this.ctaButton = root.querySelector('[data-mp-cozzee-cta]');
      this.ctaLabelEl = root.querySelector('[data-mp-cozzee-cta-label]');
      this.errorEl = root.querySelector('[data-mp-cozzee-error]');

      this.familyPicker = null;
      this.pickerAnchor = null;
      this.productForm = null;
      this.catalogRetries = 0;
      this.initialized = false;
      this.slotsRendered = { 2: false, 3: false, 4: false };
      this.openMenu = null;

      this.init();
    }

    init() {
      if (this.initialized) return;

      this.setupPicker();
      if (this.familyPicker && this.buy1Mount) {
        this.mountPicker(this.buy1Mount);
      }
      this.bindBuy1CtaSync();
      if (this.loadCatalog()) {
        this.initializeAllSlotDefaults();
        this.preRenderBundleSlots();
      }

      this.optionCards.forEach((card) => {
        card.querySelector('.mp-cozzee-option__header')?.addEventListener('click', () => {
          this.selectOption(Number(card.dataset.mpCozzeeOption));
        });
      });

      this.addSlotButton?.addEventListener('click', () => this.addSlot());
      this.ctaButton?.addEventListener('click', () => this.handleCtaClick());

      document.addEventListener('click', (event) => {
        if (!this.openMenu) return;
        if (this.openMenu.contains(event.target)) return;
        this.closeAllMenus();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeAllMenus();
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, () => this.updateCta());
      }

      this.initialized = true;
      this.selectOption(1);
      this.updateCardPrices();
      this.retryCatalogLoad();
    }

    setupPicker() {
      this.productForm = this.productInfo.querySelector('product-form');
      this.familyPicker = this.productInfo.querySelector('variant-selects[data-mp-product-family="basket"]');
      if (this.familyPicker && !this.pickerAnchor) {
        this.pickerAnchor = document.createComment('mp-cozzee-picker-anchor');
        this.familyPicker.parentNode.insertBefore(this.pickerAnchor, this.familyPicker);
      }
    }

    retryCatalogLoad() {
      if (this.catalog.length > 0) return;
      if (this.catalogRetries > 30) return;

      this.catalogRetries += 1;
      window.setTimeout(() => {
        if (this.loadCatalog()) {
          this.initializeAllSlotDefaults();
          this.preRenderBundleSlots();
          this.updateCardPrices();
          this.updateCta();
          return;
        }
        this.retryCatalogLoad();
      }, 200);
    }

    bindBuy1CtaSync() {
      const form = this.productForm?.querySelector('form');
      const submit = this.productForm?.querySelector('[type="submit"]');
      const variantInput = form?.querySelector('[name="id"]');

      variantInput?.addEventListener('change', () => {
        const buy1 = this.getBuy1Variant();
        if (buy1) this.selectionContinuity[0] = buy1;
        this.updateCta();
      });

      if (submit) {
        new MutationObserver(() => {
          if (this.tier === 1) this.updateCta();
        }).observe(submit, { attributes: true, attributeFilter: ['disabled'] });
      }

      this.productInfo.addEventListener('click', (event) => {
        if (event.target.closest('[data-mp-family-variant]')) {
          window.setTimeout(() => {
            const buy1 = this.getBuy1Variant();
            if (buy1) this.selectionContinuity[0] = buy1;
            this.updateCta();
          }, 0);
        }
      });
    }

    loadCatalog() {
      const script = this.productInfo.querySelector('[data-mp-family-catalog]');
      if (!script) return false;

      try {
        const data = JSON.parse(script.textContent);
        this.catalog = (data.variants || []).filter((variant) => variant.available !== false);
        if (this.basePrice <= 0 && this.catalog[0]) {
          this.basePrice = Number(this.catalog[0].price) || 0;
        }
        return this.catalog.length > 0;
      } catch (error) {
        return false;
      }
    }

    createDefaultSlot() {
      const variant = this.catalog[0];
      if (!variant) return { variantId: null, variant: null };
      return { variantId: variant.id, variant };
    }

    createDefaultSlots(count) {
      return Array.from({ length: count }, () => this.createDefaultSlot());
    }

    getBuy1Variant() {
      const variantId = this.productForm?.querySelector('form [name="id"]')?.value;
      if (!variantId) return null;

      const variant = this.catalog.find((entry) => String(entry.id) === String(variantId));
      if (!variant) return null;

      return { variantId: variant.id, variant };
    }

    applyBuy1FromContinuity() {
      const carried = this.normalizeSlot(this.selectionContinuity[0]);
      if (!carried?.variantId) return;

      if (typeof window.MPProductFamily?.applyVariant === 'function') {
        window.MPProductFamily.applyVariant(this.productInfo, String(carried.variantId));
        return;
      }

      this.productInfo
        .querySelector(`[data-mp-family-variant="${carried.variantId}"]`)
        ?.click();
    }

    normalizeSlot(slot) {
      if (!slot?.variantId) return null;

      const variant =
        this.catalog.find((entry) => String(entry.id) === String(slot.variantId)) || slot.variant;
      if (!variant) return null;

      return { variantId: variant.id, variant };
    }

    syncContinuityFromCurrentTier() {
      if (this.tier === 1) {
        const buy1 = this.getBuy1Variant();
        if (buy1) this.selectionContinuity[0] = buy1;
        return;
      }

      (this.slotState[this.tier] || []).forEach((slot, index) => {
        const normalized = this.normalizeSlot(slot);
        if (normalized) this.selectionContinuity[index] = normalized;
      });
    }

    buildSlotsFromContinuity(tier) {
      if (tier === 1 || !this.catalog.length) return;

      let count = tier;
      if (tier === 4) {
        count = Math.max(
          this.minSlots[4],
          this.slotState[4]?.length || 0,
          this.selectionContinuity.length
        );
      }

      const slots = [];
      for (let index = 0; index < count; index += 1) {
        slots.push(this.normalizeSlot(this.selectionContinuity[index]) || this.createDefaultSlot());
      }

      this.slotState[tier] = slots;
      this.slotsRendered[tier] = false;
    }

    initializeAllSlotDefaults() {
      if (!this.catalog.length) return;

      const buy1 = this.getBuy1Variant();
      this.selectionContinuity = buy1 ? [buy1] : [this.createDefaultSlot()];
      [2, 3, 4].forEach((tier) => this.buildSlotsFromContinuity(tier));
      this.slotsRendered = { 2: false, 3: false, 4: false };
    }

    preRenderBundleSlots() {
      if (!this.catalog.length) return;

      const activeTier = this.tier;
      [2, 3, 4].forEach((tier) => {
        this.tier = tier;
        this.buildSlotsFromContinuity(tier);
        this.renderSlots(true);
      });
      this.tier = activeTier;
    }

    needsSlotRerender(tier) {
      const container = this.root.querySelector(`[data-mp-cozzee-slots="${tier}"]`);
      const slots = this.slotState[tier] || [];
      if (!container) return true;
      return container.childElementCount !== slots.length;
    }

    ensureSlotDefaults(tier) {
      this.buildSlotsFromContinuity(tier);
    }

    setSlideImages(slide, imageUrl, alt) {
      if (!slide || !imageUrl) return;

      slide.querySelectorAll('img').forEach(function (img) {
        img.src = imageUrl;
        img.removeAttribute('data-mp-defer-src');
        img.removeAttribute('data-mp-defer-srcset');
        img.srcset = '';
        if (alt) img.alt = alt;
        img.dataset.mpLoaded = 'true';
        img.classList.remove('mp-deferred-media');
      });

      const spinner = slide.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    }

    updateMainImage(variant) {
      if (!variant?.featuredImageUrl || this.tier === 1) return;

      const gallery = this.productInfo.querySelector('media-gallery');
      if (!gallery) return;

      const viewer = gallery.querySelector('[id^="GalleryViewer"]');
      const firstSlide = viewer?.querySelector('.product__media-item');
      if (!firstSlide) return;

      this.setSlideImages(firstSlide, variant.featuredImageUrl, variant.displayLabel);

      const thumbList = gallery.querySelector('[id^="Slider-Thumbnails"]');
      const firstThumb = thumbList?.querySelector('.thumbnail-list__item');
      if (firstThumb) {
        const thumbImg = firstThumb.querySelector('img');
        if (thumbImg) {
          thumbImg.src = variant.featuredImageUrl;
          thumbImg.removeAttribute('srcset');
          thumbImg.alt = variant.displayLabel || '';
        }
      }

      viewer.querySelectorAll('.product__media-item').forEach(function (slide) {
        slide.classList.remove('is-active');
      });
      firstSlide.classList.add('is-active');

      if (thumbList) {
        thumbList.querySelectorAll('.thumbnail-list__item button').forEach(function (button) {
          button.removeAttribute('aria-current');
        });
        firstThumb?.querySelector('button')?.setAttribute('aria-current', 'true');
      }

      // Skip gallery.setActiveMedia — it scrolls the page to the hero image.
    }

    isMobileViewport() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    scrollToMainGallery() {
      const gallery = this.productInfo.querySelector('media-gallery');
      const viewer = gallery?.querySelector('[id^="GalleryViewer"]');
      const target = viewer || gallery;
      if (!target) return;

      const stickyHeader = document.querySelector('sticky-header');
      const headerOffset = stickyHeader ? stickyHeader.offsetHeight : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 12;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth',
      });
    }

    bindSlotMediaPreview(media, index) {
      if (!media) return;

      media.setAttribute('role', 'button');
      media.setAttribute('tabindex', '0');

      const activatePreview = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const variant = this.slots[index]?.variant;
        if (!variant?.featuredImageUrl) return;

        this.updateMainImage(variant);

        if (this.isMobileViewport()) {
          this.scrollToMainGallery();
        }
      };

      media.addEventListener('click', activatePreview);
      media.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          activatePreview(event);
        }
      });

      this.updateSlotMediaLabel(media, index);
    }

    updateSlotMediaLabel(media, index) {
      if (!media) return;
      const label = this.slots[index]?.variant?.displayLabel;
      media.setAttribute('aria-label', label ? `View ${label} in gallery` : 'View print in gallery');
    }

    get slots() {
      if (this.tier === 1) return [];
      return this.slotState[this.tier] || [];
    }

    set slots(value) {
      if (this.tier === 1) return;
      this.slotState[this.tier] = value;
    }

    selectOption(tier) {
      this.closeAllMenus();
      if (tier !== this.tier) {
        this.syncContinuityFromCurrentTier();
      }
      this.tier = tier;
      this.clearError();
      this.productInfo.setAttribute('data-mp-cozzee-mode', String(tier));

      this.optionCards.forEach((card) => {
        const cardTier = Number(card.dataset.mpCozzeeOption);
        const selected = cardTier === tier;
        card.classList.toggle('mp-cozzee-option--selected', selected);
        const header = card.querySelector('.mp-cozzee-option__header');
        const body = card.querySelector('.mp-cozzee-option__body');
        header?.setAttribute('aria-expanded', selected ? 'true' : 'false');
        if (body) body.hidden = !selected;
      });

      if (tier === 1) {
        this.setPickerVisible(true);
        if (this.addSlotButton) this.addSlotButton.hidden = true;
        this.applyBuy1FromContinuity();
      } else {
        this.setPickerVisible(false);
        if (this.addSlotButton) this.addSlotButton.hidden = tier !== 4;
        this.buildSlotsFromContinuity(tier);
        this.renderSlots(true);
        const previewVariant = this.slots[this.slots.length - 1]?.variant;
        if (previewVariant) this.updateMainImage(previewVariant);
      }

      this.updateCardPrices();
      this.updateCta();
    }

    restorePickerHome() {
      if (!this.familyPicker || !this.pickerAnchor?.parentNode) return;
      this.pickerAnchor.parentNode.insertBefore(this.familyPicker, this.pickerAnchor.nextSibling);
    }

    mountPicker(target) {
      if (!this.familyPicker || !target) return;
      if (this.familyPicker.parentElement !== target) {
        target.appendChild(this.familyPicker);
      }
      this.setPickerVisible(this.tier === 1);
    }

    setPickerVisible(visible) {
      if (!this.familyPicker) return;
      this.familyPicker.classList.toggle('mp-cozzee-picker-stashed', !visible);
    }

    stashPicker() {
      this.setPickerVisible(false);
    }

    updateCardPrices() {
      const symbol = this.currencySymbol;
      const base = this.basePrice;

      const price1 = this.root.querySelector('[data-mp-cozzee-option-price="1"]');
      if (price1) {
        price1.textContent = formatMoney(base, symbol);
      }

      [2, 3, 4].forEach((tier) => {
        const saleEl = this.root.querySelector(`[data-mp-cozzee-option-price="${tier}"]`);
        const compareEl = this.root.querySelector(`[data-mp-cozzee-option-compare="${tier}"]`);
        const eachEl = this.root.querySelector(`[data-mp-cozzee-option-each="${tier}"]`);
        if (!saleEl) return;

        const count = tier === 4 ? (this.tier === 4 ? Math.max(4, this.slots.length) : 4) : tier;
        const saleTotal = bundleTotalCents(base, count);
        const compareTotal = base * count;
        const eachCents = pricePerBagCents(base, count);

        saleEl.textContent = formatMoney(saleTotal, symbol);
        if (compareEl) {
          compareEl.textContent = formatMoney(compareTotal, symbol);
        }
        if (eachEl) {
          eachEl.textContent = `${formatMoney(eachCents, symbol)} each`;
        }
      });
    }

    slotsContainer() {
      return this.root.querySelector(`[data-mp-cozzee-slots="${this.tier}"]`);
    }

    renderSlots(force) {
      const container = this.slotsContainer();
      if (!container) return;

      if (
        !force &&
        this.slotsRendered[this.tier] &&
        container.childElementCount === this.slots.length
      ) {
        return;
      }

      container.replaceChildren();
      this.slots.forEach((slot, index) => {
        container.appendChild(this.createSlotElement(slot, index));
      });
      this.slotsRendered[this.tier] = true;
    }

    canRemoveSlot(index) {
      return this.tier === 4 && index >= this.minSlots[4] && this.slots.length > this.minSlots[4];
    }

    removeSlot(index) {
      if (!this.canRemoveSlot(index)) return;
      this.closeAllMenus();
      this.slots = this.slots.filter((_, i) => i !== index);
      this.syncContinuityFromCurrentTier();
      this.selectionContinuity.length = this.slots.length;
      this.renderSlots(true);
      this.updateCardPrices();
      this.updateCta();
      this.clearError();
    }

    updateSlotUI(index) {
      const container = this.slotsContainer();
      if (!container) return;

      const slotEl = container.querySelector(`[data-slot-index="${index}"]`);
      if (!slotEl) return;

      const slot = this.slots[index];
      const media = slotEl.querySelector('.mp-cozzee-slot__media');
      const trigger = slotEl.querySelector('.mp-cozzee-slot__trigger');

      if (trigger) {
        this.updateSlotTrigger(trigger, slot);
      }

      if (media && slot.variant?.featuredImageUrl) {
        let img = media.querySelector('img');
        if (!img) {
          media.innerHTML = '';
          img = document.createElement('img');
          img.loading = 'lazy';
          img.width = 96;
          img.height = 96;
          media.appendChild(img);
        }
        img.src = slot.variant.featuredImageUrl;
        img.alt = slot.variant.displayLabel || '';
        this.updateSlotMediaLabel(media, index);
      }

      slotEl.querySelectorAll('.mp-family-picker__swatch').forEach((swatch) => {
        const active = Number(swatch.dataset.variantId) === slot.variantId;
        swatch.classList.toggle('mp-family-picker__swatch--active', active);
        swatch.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    updateSlotTrigger(trigger, slot) {
      let label = trigger.querySelector('.mp-cozzee-slot__trigger-label');
      if (!label) {
        label = document.createElement('span');
        label.className = 'mp-cozzee-slot__trigger-label';
        trigger.prepend(label);
      }

      label.innerHTML = '';
      label.append('Color: ');

      const value = document.createElement('span');
      value.dataset.slotValue = 'true';
      value.textContent = slot.variant?.displayLabel || 'Select print';
      label.appendChild(value);

      if (!trigger.querySelector('.mp-cozzee-slot__chevron')) {
        const chevron = document.createElement('span');
        chevron.className = 'mp-cozzee-slot__chevron';
        chevron.setAttribute('aria-hidden', 'true');
        trigger.appendChild(chevron);
      }
    }

    createFamilySwatch(variant, slot, index) {
      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = 'mp-photo-swatch-label mp-family-picker__swatch';
      swatch.dataset.variantId = String(variant.id);
      swatch.setAttribute('aria-label', variant.displayLabel || 'Print');
      swatch.setAttribute('aria-pressed', variant.id === slot.variantId ? 'true' : 'false');

      if (variant.id === slot.variantId) {
        swatch.classList.add('mp-family-picker__swatch--active');
      }

      if (variant.available === false) {
        swatch.classList.add('mp-family-picker__swatch--unavailable');
        swatch.disabled = true;
      }

      if (variant.featuredImageUrl) {
        swatch.innerHTML = `<span class="mp-photo-swatch-label__image"><img src="${variant.featuredImageUrl}" alt="" loading="lazy" width="120" height="120"></span>`;
      }

      swatch.addEventListener('click', (event) => {
        event.stopPropagation();
        if (swatch.disabled) return;
        this.selectVariant(index, variant);
      });

      return swatch;
    }

    setMenuOpenOption(picker) {
      this.root.querySelectorAll('.mp-cozzee-option--menu-open').forEach((card) => {
        card.classList.remove('mp-cozzee-option--menu-open');
      });
      picker?.closest('[data-mp-cozzee-option]')?.classList.add('mp-cozzee-option--menu-open');
    }

    closeAllMenus() {
      this.root.querySelectorAll('.mp-cozzee-slot__menu').forEach((menu) => {
        menu.hidden = true;
      });
      this.root.querySelectorAll('.mp-cozzee-slot__trigger').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
      this.root.querySelectorAll('.mp-cozzee-slot__picker').forEach((picker) => {
        picker.classList.remove('mp-cozzee-slot__picker--open');
      });
      this.root.querySelectorAll('.mp-cozzee-option--menu-open').forEach((card) => {
        card.classList.remove('mp-cozzee-option--menu-open');
      });
      this.openMenu = null;
    }

    createSlotElement(slot, index) {
      const wrap = document.createElement('div');
      wrap.className = 'mp-cozzee-slot mp-cozzee-slot--inline';
      wrap.dataset.slotIndex = String(index);

      const card = document.createElement('div');
      card.className = 'mp-cozzee-slot__card';

      const media = document.createElement('div');
      media.className = 'mp-cozzee-slot__media';
      if (slot.variant?.featuredImageUrl) {
        const alt = slot.variant.displayLabel || '';
        media.innerHTML = `<img src="${slot.variant.featuredImageUrl}" alt="${alt.replace(/"/g, '&quot;')}" loading="lazy" width="96" height="96">`;
      }
      this.bindSlotMediaPreview(media, index);

      const panel = document.createElement('div');
      panel.className = 'mp-cozzee-slot__panel';

      const picker = document.createElement('div');
      picker.className = 'mp-cozzee-slot__picker';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'mp-cozzee-slot__trigger';
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-haspopup', 'listbox');
      this.updateSlotTrigger(trigger, slot);

      const menu = document.createElement('div');
      menu.className = 'mp-cozzee-slot__menu';
      menu.hidden = true;
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-label', `Print options for item ${index + 1}`);

      const fieldset = document.createElement('fieldset');
      fieldset.className =
        'js product-form__input product-form__input--photo-swatch mp-family-picker mp-family-picker--photo mp-cozzee-slot__family-picker';

      const swatches = document.createElement('div');
      swatches.className = 'mp-family-picker__swatches';

      this.catalog.forEach((variant) => {
        swatches.appendChild(this.createFamilySwatch(variant, slot, index));
      });

      fieldset.appendChild(swatches);
      menu.appendChild(fieldset);

      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const isOpen = !menu.hidden;
        this.closeAllMenus();
        if (!isOpen) {
          menu.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
          picker.classList.add('mp-cozzee-slot__picker--open');
          this.openMenu = picker;
          this.setMenuOpenOption(picker);
        }
      });

      picker.appendChild(trigger);
      picker.appendChild(menu);

      const title = document.createElement('p');
      title.className = 'mp-cozzee-slot__title';
      title.textContent = `Basket ${index + 1}`;

      panel.appendChild(title);
      panel.appendChild(picker);

      if (this.canRemoveSlot(index)) {
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'mp-cozzee-slot__remove';
        removeBtn.textContent = 'Remove';
        removeBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.removeSlot(index);
        });
        panel.appendChild(removeBtn);
      }

      card.appendChild(media);
      card.appendChild(panel);
      wrap.appendChild(card);
      return wrap;
    }

    selectVariant(index, variant) {
      const next = this.slots.slice();
      next[index] = { variantId: variant.id, variant };
      this.slots = next;
      this.selectionContinuity[index] = next[index];
      this.closeAllMenus();
      this.updateSlotUI(index);
      this.updateMainImage(variant);
      this.updateCta();
      this.clearError();
    }

    addSlot() {
      if (this.tier !== 4) return;
      const newSlot = this.createDefaultSlot();
      this.slots = this.slots.concat(newSlot);
      this.selectionContinuity[this.slots.length - 1] = newSlot;
      this.renderSlots(true);
      this.updateCardPrices();
      this.updateCta();
    }

    updateCta() {
      const symbol = this.currencySymbol;
      const addToCartLabel = window.variantStrings?.addToCart || 'Add to cart';

      if (this.tier === 1) {
        const nativeSubmit = this.productForm?.querySelector('[type="submit"]');
        const unavailable = nativeSubmit?.disabled === true;

        if (this.ctaLabelEl) {
          this.ctaLabelEl.textContent = addToCartLabel;
        }
        if (this.ctaButton) {
          this.ctaButton.disabled = unavailable;
        }
        return;
      }

      const count = this.slots.length;
      const total = bundleTotalCents(this.basePrice, count);
      const allSelected = this.slots.every((slot) => slot.variantId);

      if (this.ctaLabelEl) {
        this.ctaLabelEl.textContent = allSelected
          ? `Add ${count} to cart — ${formatMoney(total, symbol)}`
          : 'Add bundle to cart';
      }

      if (this.ctaButton) {
        this.ctaButton.disabled = !allSelected;
      }
    }

    handleCtaClick() {
      if (this.tier === 1) {
        const submit = this.productForm?.querySelector('[type="submit"]');
        if (submit && !submit.disabled) submit.click();
        return;
      }
      this.addBundleToCart();
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    }

    addBundleToCart() {
      if (this.tier === 1) return;

      const items = aggregateItems(this.slots);
      if (items.length === 0 || items.reduce((sum, item) => sum + item.quantity, 0) !== this.slots.length) {
        this.showError('Please select a print for each slot.');
        return;
      }

      const cart = document.querySelector('cart-drawer');
      const spinner = this.ctaButton?.querySelector('.loading__spinner');

      if (this.ctaButton) {
        this.ctaButton.disabled = true;
        this.ctaButton.classList.add('loading');
      }
      spinner?.classList.remove('hidden');
      this.clearError();

      const payload = { items };

      if (cart) {
        payload.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        payload.sections_url = window.location.pathname;
        cart.setActiveElement(this.ctaButton);
      }

      const config = fetchConfig('json');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify(payload);

      fetch(window.routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || response.message || 'Unable to add bundle to cart.');
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'mp-cozzee-bundle-selector',
                errors: response.description,
                message: response.message,
              });
            }
            return;
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          const startMarker =
            typeof CartPerformance !== 'undefined'
              ? CartPerformance.createStartingMarker('add:wait-for-subscribers')
              : null;

          const renderDrawer = () => {
            if (typeof CartPerformance !== 'undefined') {
              CartPerformance.measure('add:paint-updated-sections', () => {
                cart.renderContents(response);
              });
            } else {
              cart.renderContents(response);
            }
          };

          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'mp-cozzee-bundle-selector',
              cartData: response,
            }).then(() => {
              if (startMarker && typeof CartPerformance !== 'undefined') {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              }
              renderDrawer();
            });
          } else {
            renderDrawer();
          }
        })
        .catch(() => {
          this.showError('Network error. Please try again.');
        })
        .finally(() => {
          if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          this.ctaButton?.classList.remove('loading');
          spinner?.classList.add('hidden');
          this.updateCta();
        });
    }
  }

  function boot() {
    document.querySelectorAll('[data-mp-cozzee-bundle-selector]').forEach((root) => {
      if (root.dataset.mpCozzeeInitialized === 'true') return;
      root.dataset.mpCozzeeInitialized = 'true';
      new CozzeeBundleSelector(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
